import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { ThemeProvider } from '@/context/ThemeContext'
import { NotificationProvider } from '@/context/NotificationContext'
import { RestaurantProvider } from '@/context/RestaurantContext'
import LoginScreen from '@/screens/auth/LoginScreen'
import AdminNavigator from '@/navigation/AdminNavigator'
import RestaurantNavigator from '@/navigation/RestaurantNavigator'
import LoadingScreen from '@/components/common/LoadingScreen'
import PWAInstallPrompt from '@/components/common/PWAInstallPrompt'
import UpdatePrompt from '@/components/common/UpdatePrompt'
import { dailyNotificationScheduler } from '@/services/dailyNotificationScheduler'
import { notificationManager } from '@/services/pushNotificationService'

function AppContent() {
  const { user, isAuthenticated, isLoading } = useAuth()


  // Initialize notifications and daily scheduler for restaurant owners
  useEffect(() => {
    if (isAuthenticated && user?.userType === 'restaurant_owner') {
      // Initialize push notifications
      notificationManager.initialize().then((initialized) => {
        if (initialized) {
          console.log('✅ Push notifications initialized')

          // Start the daily notification scheduler
          dailyNotificationScheduler.start()

          const nextInfo = dailyNotificationScheduler.getNextNotificationInfo()
          console.log(`⏰ Next daily reminder scheduled for ${nextInfo.time.toLocaleString()}`)
        } else {
          console.warn('⚠️ Push notifications not initialized - user may have denied permission')
        }
      })

      // Cleanup on unmount
      return () => {
        dailyNotificationScheduler.stop()
      }
    }
  }, [isAuthenticated, user?.userType])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // Route based on user type
  if (user?.userType === 'admin') {
    return <AdminNavigator />
  } else if (user?.userType === 'restaurant_owner') {
    return <RestaurantNavigator />
  }

  // Fallback - should not reach here
  return <Navigate to="/login" replace />
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RestaurantProvider>
          <NotificationProvider>
            <Router>
              <div className="min-h-screen bg-background text-foreground">
                <AppContent />
                <PWAInstallPrompt />
                <UpdatePrompt />
                <Toaster richColors position="top-right" />
              </div>
            </Router>
          </NotificationProvider>
        </RestaurantProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App